import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { ArrowLeft, Search, SearchX, SlidersHorizontal, X } from "lucide-react";

import { ModeToggle } from "@/components/ModeToggle";
import {
  BusinessEventCard,
  BusinessListingCard,
  CardSkeleton,
  EmptyState,
  ErrorState,
  EventSkeleton,
  ServiceCard,
  T,
} from "@/components/business/BusinessDirectory";
import { CategoryTileGrid, CategoryTileGridSkeleton } from "@/components/business/BusinessMobile";
import { BusinessStaysView } from "@/components/business/BusinessStaysView";
import { TransportMobility } from "@/components/business/TransportMobility";
import { AutoT } from "@/i18n/AutoT";
import { useLanguage } from "@/i18n/LanguageContext";
import { useCity } from "@/context/CityContext";
import { useResolvedCity } from "@/hooks/use-public-cities";
import { businessGetPublicCategory } from "@/lib/business-public.functions";
import { usePublicPlaces } from "@/hooks/use-public-places";
import { laneForCategoryKey, laneItems } from "@/lib/business-lanes";
import { PlaceCard } from "@/components/PlaceCard";
import { businessTrackEvent } from "@/lib/business-track.functions";
import { analyticsSessionId } from "@/lib/analytics-session";

export const Route = createFileRoute("/business/category/$key")({
  head: ({ params }) => {
    const pretty = params.key
      .replace(/[_-]+/g, " ")
      .replace(/\b\w/g, (c) => c.toUpperCase());
    const url = `https://whatfinland.com/business/category/${params.key}`;
    const description = `Browse ${pretty.toLowerCase()} places, services and events across Finland — filtered by your city.`;
    return {
      meta: [
        { title: `${pretty} In Finland — what Finland` },
        { name: "description", content: description },
        { property: "og:title", content: `${pretty} In Finland — what Finland` },
        { property: "og:description", content: description },
        { property: "og:type", content: "website" },
        { property: "og:url", content: url },
        { name: "twitter:card", content: "summary_large_image" },
      ],
      links: [{ rel: "canonical", href: url }],
    };
  },
  component: BusinessCategoryPage,
});

function isStaysKey(key: string) {
  return key === "stays" || key === "business-stays" || key === "hotels" || key === "accommodation";
}

function isTransportKey(key: string) {
  return key === "transport" || key === "transport-mobility" || key === "mobility";
}

function isCustomCategoryLayout(key: string) {
  return isStaysKey(key) || isTransportKey(key);
}

function BusinessCategoryPage() {
  const { key } = Route.useParams();
  const navigate = useNavigate();
  const { lang } = useLanguage();
  const { city } = useCity();
  const cityName = useResolvedCity(city).name;

  const loadFn = useServerFn(businessGetPublicCategory);
  const trackFn = useServerFn(businessTrackEvent);
  const track = (args: { data: Record<string, unknown> }) =>
    trackFn({ data: { ...args.data, session_id: analyticsSessionId() } as never }).catch(() => {});

  const query = useQuery({
    queryKey: ["business-category", key],
    queryFn: () => loadFn({ data: { key } }),
  });

  const [q, setQ] = useState("");
  const [showCategories, setShowCategories] = useState(true);
  const needle = q.trim().toLowerCase();

  const category = query.data?.category ?? null;
  const categories = query.data?.categories ?? [];

  const pick = (en?: string | null, fi?: string | null) =>
    lang === "fi" && fi ? { text: fi, raw: true } : { text: en ?? "", raw: false };

  const inCity = (destinations: string[] | null | undefined) =>
    !destinations || destinations.length === 0 || destinations.includes(city);

  const listings = useMemo(
    () =>
      (query.data?.listings ?? []).filter((l) => {
        if (!inCity(l.destinations)) return false;
        if (!needle) return true;
        return [l.name, l.summary_en, l.summary_fi, l.address, (l.tags ?? []).join(" ")]
          .filter(Boolean)
          .join(" ")
          .toLowerCase()
          .includes(needle);
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [query.data, city, needle],
  );

  const services = useMemo(
    () =>
      (query.data?.services ?? []).filter((s) => {
        if (!inCity(s.destinations)) return false;
        if (!needle) return true;
        return [s.name_en, s.name_fi, s.description_en, s.description_fi]
          .filter(Boolean)
          .join(" ")
          .toLowerCase()
          .includes(needle);
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [query.data, city, needle],
  );

  const events = useMemo(
    () =>
      (query.data?.events ?? []).filter((e) => {
        if (e.destination && e.destination !== city) return false;
        if (!needle) return true;
        return [e.title_en, e.title_fi, e.venue_name].filter(Boolean).join(" ").toLowerCase().includes(needle);
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [query.data, city, needle],
  );

  // ---- curated places (Business admin → Lanes) ----
  // Lane picks are places, not business_listings, so they must be resolved
  // against the public places feed — otherwise a hand-curated lane renders
  // empty on its category page.
  const allPlaces = usePublicPlaces();
  const lanePicks = query.data?.lanePicks ?? {};
  const places = useMemo(() => {
    const lane = laneForCategoryKey(key);
    if (!lane) return [];
    const index = new Map(allPlaces.map((i) => [i.id, i]));
    const picked = lanePicks[lane.key];
    const base =
      picked && picked.length > 0
        ? (picked.map((id) => index.get(id)).filter(Boolean) as typeof allPlaces)
        : laneItems(
            allPlaces.filter((i) => i.city === city),
            lane,
          );
    if (!needle) return base;
    return base.filter((i) =>
      [i.name, i.category, i.area, i.description].filter(Boolean).join(" ").toLowerCase().includes(needle),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allPlaces, key, city, needle, JSON.stringify(lanePicks)]);

  const nothing =
    listings.length === 0 && services.length === 0 && events.length === 0 && places.length === 0;
  const isTransport = key === "transport" || key === "transport-mobility";
  const isStays = isStaysKey(key);
  const prettyKey = (() => {
    const s = key.replace(/[_-]+/g, " ").trim();
    return s.charAt(0).toUpperCase() + s.slice(1);
  })();
  const title = category ? pick(category.name_en, category.name_fi) : { text: prettyKey, raw: true };
  const blurb = category
    ? pick((category as any).description_en, (category as any).description_fi)
    : { text: "", raw: false };

  return (
    <div className="pb-16">
      <ModeToggle mode="business" />

      {/* HERO BAND */}
      <div className="relative overflow-hidden bg-navy text-white">
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0 opacity-[0.35]"
          style={{
            background:
              "radial-gradient(60% 90% at 12% 0%, rgba(255,255,255,0.22) 0%, transparent 60%), radial-gradient(50% 80% at 95% 20%, rgba(255,255,255,0.14) 0%, transparent 65%)",
          }}
        />
        <div className="relative mx-auto w-full max-w-5xl px-4 pb-16 pt-5 sm:px-6 sm:pb-20 sm:pt-6">
          <button
            type="button"
            onClick={() => navigate({ to: "/business" })}
            style={{ textTransform: "none" }}
            className="inline-flex items-center gap-1.5 text-[13px] font-semibold text-white/70 transition-colors hover:text-white"
          >
            <ArrowLeft className="size-4" />
            <AutoT text="All categories" />
          </button>

          <header className="mt-4 grid grid-cols-[minmax(0,1fr)_auto] items-end gap-4">
            <div className="min-w-0">
              <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-white/55">
                <AutoT text="What Business" />
              </p>
              <h1
                style={{ textTransform: "none" }}
                className="font-heading mt-1.5 text-[30px] font-bold leading-[1.05] sm:text-[44px]"
              >
                <T v={title} />
              </h1>
              <p className="mt-2.5 max-w-xl text-[14px] leading-relaxed text-white/70">
                {blurb.text ? (
                  <T v={blurb} />
                ) : (
                  <AutoT text={`Everything published in this category for ${cityName}.`} />
                )}
              </p>
            </div>

            <div className="hidden shrink-0 gap-2 sm:flex">
              {listings.length > 0 ? (
                <div className="rounded-2xl bg-white/10 px-4 py-3 text-center ring-1 ring-white/15 backdrop-blur-sm">
                  <div className="font-heading text-2xl font-bold leading-none">{listings.length}</div>
                  <div className="mt-1 text-[10px] font-semibold uppercase tracking-[0.14em] text-white/60">
                    <AutoT text="Places" />
                  </div>
                </div>
              ) : null}
              {events.length > 0 ? (
                <div className="rounded-2xl bg-white/10 px-4 py-3 text-center ring-1 ring-white/15 backdrop-blur-sm">
                  <div className="font-heading text-2xl font-bold leading-none">{events.length}</div>
                  <div className="mt-1 text-[10px] font-semibold uppercase tracking-[0.14em] text-white/60">
                    <AutoT text="Events" />
                  </div>
                </div>
              ) : null}
            </div>
          </header>
        </div>
      </div>

      <div className="mx-auto w-full max-w-5xl px-4 sm:px-6">


        {/* SEARCH + CATEGORY RAIL — hidden for stays/transport so the custom page layout matches the reference */}
        {!isCustomCategoryLayout(key) ? (
          <div className="sticky top-0 z-20 -mx-4 -mt-12 rounded-t-3xl bg-background/90 px-4 pb-3 pt-4 backdrop-blur-md sm:mx-0 sm:rounded-3xl sm:px-5 sm:pb-4 sm:shadow-[0_18px_40px_-24px_rgba(16,24,40,0.45)]">
            <div className="flex items-center gap-2.5 rounded-full border border-navy/10 bg-white px-4 py-2.5 shadow-[0_1px_3px_rgba(16,24,40,0.06)] sm:px-5 sm:py-3">
              <Search className="size-[18px] shrink-0 text-navy/50" strokeWidth={2.25} />
              <input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder={lang === "fi" ? "Hae tästä kategoriasta..." : "Search in this category..."}
                aria-label="Search this category"
                type="search"
                enterKeyHint="search"
                className="font-body h-6 min-w-0 flex-1 bg-transparent text-[16px] tracking-[-0.01em] text-navy outline-none placeholder:text-navy/40 sm:text-[15px] [&::-webkit-search-cancel-button]:hidden"
              />
              {needle ? (
                <button
                  type="button"
                  onClick={() => setQ("")}
                  aria-label="Clear search"
                  className="grid size-7 shrink-0 place-items-center rounded-full bg-navy/5 text-navy transition-colors hover:bg-navy hover:text-white"
                >
                  <X className="size-3.5" />
                </button>
              ) : null}

              <button
                type="button"
                onClick={() => setShowCategories((v) => !v)}
                aria-expanded={showCategories}
                aria-label="Toggle categories"
                className={`grid size-7 shrink-0 place-items-center rounded-full transition-colors ${
                  showCategories ? "bg-navy text-white" : "text-navy/70 hover:bg-navy/5"
                }`}
              >
                <SlidersHorizontal className="size-[18px]" strokeWidth={2.25} />
              </button>
            </div>
          </div>
        ) : null}

        {!isCustomCategoryLayout(key) && showCategories ? (
          <div className="mt-3 sm:mt-4">
            {query.isLoading ? (
              <CategoryTileGridSkeleton />
            ) : (
              <CategoryTileGrid
                categories={categories}
                activeKey={category?.key}
                onSelect={(cat) => {
                  if (!cat) {
                    navigate({ to: "/business" });
                    return;
                  }
                  void track({ data: { event_type: "category_view" as never, destination: city, category_id: cat.id } });
                  navigate({ to: "/business/category/$key", params: { key: cat.key } });
                }}
              />
            )}
          </div>
        ) : null}

        {query.isError ? (
          <div className="mt-6">
            <ErrorState
              title="We couldn't load this category."
              onRetry={() => void query.refetch()}
              retrying={query.isFetching}
            />
          </div>
        ) : null}

        {query.isLoading ? (
          <div className="mt-8 space-y-6">
            <CardSkeleton count={6} />
            <EventSkeleton />
          </div>
        ) : !category ? (
          <div className="mt-8">
            <EmptyState
              icon={<SearchX className="size-6" />}
              title="Category not found"
              description="This category may have been renamed or hidden."
              actionLabel="Browse all categories"
              onAction={() => navigate({ to: "/business" })}
            />
          </div>
        ) : nothing && !isTransport ? (
          <div className="mt-8">
            <EmptyState
              icon={<SearchX className="size-6" />}
              title={`Nothing here for ${cityName} yet`}
              description="Try clearing your search or switching city."
              actionLabel={needle ? "Clear search" : undefined}
              onAction={needle ? () => setQ("") : undefined}
            />
          </div>
        ) : (
          <>
            {isTransport ? <TransportMobility query={q} listings={listings} services={services} /> : null}
            {isStays && listings.length > 0 ? (
              <BusinessStaysView
                listings={listings}
                onOpen={(item) =>
                  void track({
                    data: {
                      event_type: "click" as never,
                      destination: city,
                      listing_id: item.id,
                    },
                  })
                }
              />
            ) : listings.length > 0 ? (
              <section className="mt-8">
                <h2 className="font-heading text-[15px] font-bold text-navy">
                  <AutoT text="Places" />
                </h2>
                <div className="mt-3 grid grid-cols-2 gap-3 sm:gap-5 lg:grid-cols-3">
                  {listings.map((l) => (
                    <BusinessListingCard
                      key={l.id}
                      listing={l}
                      onOpen={(item) =>
                        void track({
                          data: {
                            event_type: "click" as never,
                            destination: city,
                            listing_id: item.id,
                          },
                        })
                      }
                    />
                  ))}
                </div>
              </section>
            ) : null}

            {places.length > 0 ? (
              <section className="mt-8">
                <h2 className="font-heading text-[15px] font-bold text-navy">
                  <AutoT text="Recommended places" />
                </h2>
                <div className="mt-3 grid grid-cols-2 gap-3 sm:gap-5 lg:grid-cols-3">
                  {places.slice(0, 24).map((i) => (
                    <PlaceCard key={i.id} item={i} compact />
                  ))}
                </div>
              </section>
            ) : null}

            {services.length > 0 ? (
              <section className="mt-10">
                <h2 className="font-heading text-[15px] font-bold text-navy">
                  <AutoT text="Services" />
                </h2>
                <div className="mt-3 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                  {services.map((s) => (
                    <ServiceCard key={s.id} service={s} />
                  ))}
                </div>
              </section>
            ) : null}

            {events.length > 0 ? (
              <section className="mt-10">
                <h2 className="font-heading text-[15px] font-bold text-navy">
                  <AutoT text="Upcoming events" />
                </h2>
                <div className="mt-3 grid gap-2.5 sm:grid-cols-2">
                  {events.map((e) => (
                    <BusinessEventCard key={e.id} event={e} />
                  ))}
                </div>
              </section>
            ) : null}
          </>
        )}
      </div>
    </div>
  );
}
